import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  Menu, 
  X,
  ChevronRight,
  ChevronDown,
  Star,
  Waves,
  Compass,
  Shield,
  Anchor,
  CalendarDays
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';
import LanguageSelector from '@/components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FaqItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="border border-[#dec5ae]/40 bg-[#FDFBF7] overflow-hidden"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#0a57a4]/[0.02] transition-colors"
        data-testid={`faq-item-${index}`}
      >
        <span className="text-sm font-semibold text-[#0a57a4] pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-[#099dbb] shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 pt-0">
              <p className="text-sm text-[#858275] leading-relaxed">{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Logos
const LOGO_HEADER = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/t54ah8ex_logo%203%20BELLA%20BARCA.png";
const LOGO_FOOTER = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/k3tg8tl5_2.png";

// Images from abellabarca.com
const HERO_IMAGES = [
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/627200df51b45980_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.46.33.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/f1952d304035d6b6_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.46.37.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/735e8eb1aa7b45f9_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.46.41.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/f702358e8de93118_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.46.54.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/2dad4c88f5fab1d7_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.46.48.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/cd6d8f5daab1ccd5_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.47.14.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/346c848172d7bf95_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.47.23.png",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/1e9a99b1845282a4_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.47.32.webp",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/4f4e245feeba24e5_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.47.41.png",
  "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/a8b80029d55d5bbf_Capture%20d%E2%80%99e%CC%81cran%202026-10-06%20a%CC%80%2015.47.45.png",
];
const BOAT_IMAGES = [
  "https://static.wixstatic.com/media/ce6ce7_70cdfbe537ff4c9c83852717150106a2~mv2.webp",
  "https://static.wixstatic.com/media/ce6ce7_81897caf3b674ddca078ee88b0caf341~mv2.webp",
  "https://static.wixstatic.com/media/ce6ce7_46a992c113d042328f3c6a16c7dfdd26~mv2.webp",
  "https://static.wixstatic.com/media/ce6ce7_ab51732b811c48cabbe1e40f35053b3c~mv2.webp",
  "https://static.wixstatic.com/media/ce6ce7_8d4abb56f0a94a4b9f80ea5b9f3e48d3~mv2.webp"
];

// Itinerary images
const ITINERARY_IMAGES = {
  puntaSpanu: "https://static.wixstatic.com/media/ce6ce7_c983c0762b484e91af7f96f038bae91c~mv2.jpg",
  plagedAregno: "https://static.wixstatic.com/media/ce6ce7_96e18271a2e24df686dd439a0caea4e1~mv2.jpeg",
  plageDeDavia: "https://static.wixstatic.com/media/ce6ce7_a182703a1bf646c7b2fbe11497115033~mv2.jpg",
  painDeSucre: "https://static.wixstatic.com/media/ce6ce7_374c0e23ef0b41ee9457a3dc1947209f~mv2.jpg",
  arinella: "https://static.wixstatic.com/media/ce6ce7_c581debc7cd345a59768a3d998bd2035~mv2.jpg",
  santAmbroggio: "https://static.wixstatic.com/media/ce6ce7_57d1f7ba0fa34e48aad5c75a73565f59~mv2.jpg"
};

const reviews = [
  { name: "Anas", date: "Juillet 2026", text: "Une expérience à renouveler absolument ! Le bateau était parfait et l'équipe de gérance est vraiment au top. Nous avons pu visiter en bateau de magnifiques criques et des plages sans aucun souci, alors qu'elles sont habituellement très difficiles d'accès en voiture ou à pied. Des paysages à couper le souffle ! Nous reviendrons à coup sûr et je recommande vivement cette entreprise." },
  { name: "Emmanuel Van Huffel", date: "Juillet 2026", text: "Très bon accueil. Explications pour prise en main du bateau claires et simples, suggestions de balades et de mouillages utiles. Superbe expérience que nous ne sommes pas prêts d'oublier. La preuve : nous y sommes retournés 2 jours après pour une seconde sortie. Nous recommandons sans hésiter." },
  { name: "Matthias-Daniel Wende", date: "Avril 2026", text: "Lucas est un contact très agréable. Il répond aux souhaits, répond rapidement et est très sympathique. Le bateau était très propre, comme neuf et entièrement équipé. Merci ! C'était une super belle journée sur la mer." },
  { name: "Alizee Blouin", date: "Août 2026", text: "Très bonne expérience ! Je recommande vivement. Nous avons apprécié notre sortie avec nos 2 enfants de 4 et 6 ans. Prestation sérieuse et la personne qui nous a accueilli est très sympathique." },
  { name: "Romain Ayme", date: "Juin 2026", text: "Un énorme merci à la personne qui s'est occupé de nous et qui nous a trouvé une solution pour partir aujourd'hui car la météo n'était pas bonne les 2 jours suivants. On rentrera dans le Var avec des souvenirs pleins la tête. Je recommande fortement. Merci beaucoup." },
  { name: "Mylène Leopold", date: "Août 2026", text: "Superbe expérience à vivre. Le contact a été facile et arrangeant, le bateau très confortable, je recommande !! Merci beaucoup." },
  { name: "Mélissa Lacroix", date: "Août 2026", text: "Le bateau était top, tout comme l'accueil, et le mode de réservation en ligne est très pratique." }
];

const itineraries = [
  { name: "Punta di Spanu", distance: "2 Nm", time: "20 min", image: ITINERARY_IMAGES.puntaSpanu, slug: "location-bateau-sans-permis-punta-di-spanu" },
  { name: "Plage d'Aregno", distance: "4 Nm", time: "45 min", image: ITINERARY_IMAGES.plagedAregno, slug: "location-bateau-sans-permis-plage-aregno" },
  { name: "Plage de Davia", distance: "6 Nm", time: "50 min", image: ITINERARY_IMAGES.plageDeDavia, slug: "location-bateau-sans-permis-plage-de-davia" },
  { name: "Pain de Sucre", distance: "4 Nm", time: "40 min", image: ITINERARY_IMAGES.painDeSucre, slug: "location-bateau-sans-permis-plage-de-pain-de-sucre" },
  { name: "Plage de l'Arinella", distance: "3 Nm", time: "35 min", image: ITINERARY_IMAGES.arinella, slug: "location-bateau-sans-permis-plage-arinella" },
  { name: "Calvi", distance: "5 Nm", time: "45 min", image: "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/c4ba5a1e18c1e26d_calvi.webp", slug: "location-bateau-sans-permis-calvi" },
  { name: "Lumio", distance: "1 Nm", time: "10 min", image: "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/c92f80facb177664_lumio.jpeg", slug: "location-bateau-sans-permis-lumio" },
  { name: "Balagne", distance: "", time: "", image: "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/89bbbf7ad3965c85_balagne.webp", slug: "location-bateau-sans-permis-balagne" },
  { name: "Ile Rousse", distance: "8 Nm", time: "1h", image: "https://customer-assets-jt897jd0.emergentagent.net/job_de656f61-7fdf-428f-b9b1-eca755063a94/artifacts/3b4a620be5d47322_ile%20rousse.jpg", slug: "location-bateau-sans-permis-ile-rousse" },
];

export default function HomePage() {
  const { lang } = useLanguage();
  const [boat, setBoat] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSeason, setActiveSeason] = useState('haute');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    fetchBoats();
    fetchPricing();
  }, []);

  // Hero slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const fetchBoats = async () => {
    try {
      const res = await fetch(`${API}/boats`);
      const data = await res.json();
      if (data.length > 0) setBoat(data[0]);
    } catch (e) {
      console.error('Error fetching boats:', e);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API}/pricing`);
      const data = await res.json();
      setPricing(data);
    } catch (e) {
      console.error('Error fetching pricing:', e);
    }
  };

  const hauteSaisonRates = pricing.filter(p => p.season === 'haute');
  const basseSaisonRates = pricing.filter(p => p.season === 'basse');

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#dec5ae]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img src={LOGO_HEADER} alt="A Bella Barca" className="h-14" />
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#bateau" className="text-[#0a57a4] hover:text-[#099dbb] transition-colors text-sm tracking-wide">{t('nav.boats', lang)}</a>
              <a href="#tarifs" className="text-[#0a57a4] hover:text-[#099dbb] transition-colors text-sm tracking-wide">{t('nav.rates', lang)}</a>
              <a href="#itineraires" className="text-[#0a57a4] hover:text-[#099dbb] transition-colors text-sm tracking-wide">{t('nav.itineraries', lang)}</a>
              <Link to="/contact" className="text-[#0a57a4] hover:text-[#099dbb] transition-colors text-sm tracking-wide">{t('nav.contact', lang)}</Link>
              <LanguageSelector />
              <Link to="/reservation">
                <Button data-testid="nav-reserve-btn" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white px-6">
                  {t('nav.book', lang)}
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-[#0a57a4]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-[#dec5ae]/30 py-4"
          >
            <div className="flex flex-col gap-4 px-4">
              <a href="#bateau" className="py-2 text-[#0a57a4]" onClick={() => setMobileMenuOpen(false)}>{t('nav.boats', lang)}</a>
              <a href="#tarifs" className="py-2 text-[#0a57a4]" onClick={() => setMobileMenuOpen(false)}>{t('nav.rates', lang)}</a>
              <a href="#itineraires" className="py-2 text-[#0a57a4]" onClick={() => setMobileMenuOpen(false)}>{t('nav.itineraries', lang)}</a>
              <Link to="/contact" className="py-2 text-[#0a57a4]" onClick={() => setMobileMenuOpen(false)}>{t('nav.contact', lang)}</Link>
              <LanguageSelector className="py-2" />
              <Link to="/reservation" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white">{t('nav.book', lang)}</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Hero background slideshow */}
        {HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${img})`,
              opacity: heroIndex === i ? 1 : 0
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/60" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-[#099dbb] text-sm tracking-[0.3em] uppercase mb-4 font-semibold">
              {t('hero.subtitle', lang)}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              {t('hero.title1', lang)}<br />{t('hero.title2', lang)}
            </h1>
            <p className="text-white/90 text-lg mb-8 leading-relaxed">
              {t('hero.description', lang)}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/reservation">
                <Button 
                  data-testid="hero-reserve-btn"
                  className="bg-[#099dbb] hover:bg-[#0a57a4] text-white px-8 py-6 text-lg"
                >
                  {t('hero.bookNow', lang)}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="#tarifs">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  {t('hero.viewRates', lang)}
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-4 sm:gap-8 mt-12 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2 text-white/80">
                <MapPin className="w-5 h-5 shrink-0" />
                <span className="text-sm">{t('hero.location', lang)}</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Clock className="w-5 h-5 shrink-0" />
                <span className="text-sm">{t('hero.halfDay', lang)}</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Shield className="w-5 h-5 shrink-0" />
                <span className="text-sm">{t('hero.noLicense', lang)}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Waves, title: { fr: "Eaux Cristallines", en: "Crystal Waters", it: "Acque Cristalline", de: "Kristallklares Wasser" }, desc: { fr: "Explorez les plus belles criques et plages de la Balagne", en: "Explore the most beautiful coves and beaches of Balagne", it: "Esplorate le più belle calette e spiagge della Balagne", de: "Entdecken Sie die schönsten Buchten und Strände der Balagne" } },
              { icon: Users, title: { fr: "En Famille", en: "Family Fun", it: "In Famiglia", de: "Familienspaß" }, desc: { fr: "Jusqu'à 5 personnes, parfait pour une sortie en famille", en: "Up to 5 people, perfect for a family outing", it: "Fino a 5 persone, perfetto per una gita in famiglia", de: "Bis zu 5 Personen, perfekt für einen Familienausflug" } },
              { icon: Compass, title: { fr: "Liberté Totale", en: "Total Freedom", it: "Libertà Totale", de: "Totale Freiheit" }, desc: { fr: "Naviguez à votre rythme, découvrez des lieux secrets", en: "Navigate at your own pace, discover secret places", it: "Navigate al vostro ritmo, scoprite luoghi segreti", de: "Fahren Sie in Ihrem eigenen Tempo, entdecken Sie geheime Orte" } }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-[#099dbb]/10 rounded-full flex items-center justify-center">
                  <feature.icon className="w-8 h-8 text-[#099dbb]" />
                </div>
                <h3 className="font-heading text-xl mb-3 text-[#0a57a4]">{feature.title[lang]}</h3>
                <p className="text-[#858275]">{feature.desc[lang]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Boat Section */}
      <section id="bateau" className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#099dbb] text-xs tracking-[0.2em] uppercase mb-4 font-semibold">{t('boat.discoverBoat', lang)}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0a57a4]">
              Le ROTO 450 S FAMILY
            </h2>
          </motion.div>

          {boat && (
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Image Gallery */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="relative rounded-sm overflow-hidden mb-4">
                  <img 
                    src={boat.images?.[activeImageIndex] || BOAT_IMAGES[activeImageIndex]} 
                    alt={boat.name}
                    className="w-full h-[400px] object-cover"
                  />
                  <span className="absolute top-4 right-4 bg-[#0a57a4] text-white text-xs px-3 py-1 rounded-sm">
                    Sans Permis
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {(boat.images || BOAT_IMAGES).slice(0, 5).map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImageIndex(i)}
                      className={`aspect-square rounded-sm overflow-hidden border-2 transition-all ${
                        activeImageIndex === i ? 'border-[#099dbb]' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`${boat.name} ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* Boat Details */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="font-heading text-3xl text-[#0a57a4] mb-2">{boat.name}</h3>
                <p className="text-[#858275] mb-6">{boat.model}</p>
                
                <p className="text-[#858275] leading-relaxed mb-8">{boat.description}</p>

                {/* Specifications */}
                <div className="bg-white p-4 sm:p-6 rounded-sm border border-[#dec5ae]/30 mb-8">
                  <h4 className="font-heading text-lg text-[#0a57a4] mb-4">{t('boat.characteristics', lang)}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 sm:gap-4">
                    {[
                      { label: t('boat.model', lang), value: "ROTO 450 S" },
                      { label: t('boat.engine', lang), value: boat.power },
                      { label: t('boat.length', lang), value: boat.length },
                      { label: t('boat.width', lang), value: "180 cm" },
                      { label: t('boat.height', lang), value: "82 cm" },
                      { label: t('boat.maxLoad', lang), value: "677 kg" },
                      { label: t('boat.year', lang), value: boat.year },
                      { label: t('boat.capacity', lang), value: `${boat.capacity} ${t('boat.persons', lang)}` }
                    ].map((spec, i) => (
                      <div key={i} className="flex justify-between py-2 border-b border-[#dec5ae]/20">
                        <span className="text-[#858275] text-sm">{spec.label}</span>
                        <span className="font-semibold text-[#0a57a4] text-sm">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Equipment */}
                <div className="mb-8">
                  <h4 className="font-heading text-lg text-[#0a57a4] mb-4">{t('boat.equipment', lang)}</h4>
                  <div className="flex flex-wrap gap-2">
                    {boat.features?.map((feature, j) => (
                      <span key={j} className="text-sm bg-[#dec5ae]/30 px-4 py-2 rounded-sm text-[#0a57a4]">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <Link to="/reservation">
                  <Button 
                    data-testid="reserve-boat-btn"
                    className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white py-6 text-lg"
                  >
                    {t('boat.bookThis', lang)}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifs" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-[#099dbb] text-xs tracking-[0.2em] uppercase mb-4 font-semibold">{t('pricing.title', lang)}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0a57a4] mb-4">
              {t('pricing.subtitle', lang)}
            </h2>
            <p className="text-[#858275]">{t('boat.fuelDeposit', lang)}</p>
          </motion.div>

          {/* Season toggle */}
          <div className="flex justify-center mb-12 px-2">
            <div className="inline-flex flex-col sm:flex-row bg-[#dec5ae]/20 p-1 rounded-sm w-full sm:w-auto">
              <button
                onClick={() => setActiveSeason('haute')}
                className={`px-4 sm:px-6 py-3 text-sm font-medium transition-all rounded-sm whitespace-nowrap ${
                  activeSeason === 'haute' 
                    ? 'bg-[#0a57a4] text-white' 
                    : 'text-[#858275] hover:text-[#0a57a4]'
                }`}
                data-testid="season-haute-btn"
              >
                {t('pricing.highSeason', lang)}
              </button>
              <button
                onClick={() => setActiveSeason('basse')}
                className={`px-4 sm:px-6 py-3 text-sm font-medium transition-all rounded-sm whitespace-nowrap ${
                  activeSeason === 'basse' 
                    ? 'bg-[#0a57a4] text-white' 
                    : 'text-[#858275] hover:text-[#0a57a4]'
                }`}
                data-testid="season-basse-btn"
              >
                {t('pricing.lowSeason', lang)}
              </button>
            </div>
          </div>

          {/* Pricing cards */}
          {activeSeason === 'haute' ? (
            <div className="grid md:grid-cols-3 gap-6" data-testid="haute-saison-pricing">
              {hauteSaisonRates.map((rate, i) => (
                <motion.div
                  key={rate.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#FDFBF7] p-8 text-center rounded-sm border border-[#dec5ae]/30 hover:border-[#099dbb] transition-colors"
                >
                  <h3 className="font-heading text-2xl text-[#0a57a4] mb-2">{rate.duration_label}</h3>
                  <p className="text-sm text-[#858275] mb-4">{rate.time_slot}</p>
                  <p className="font-heading text-5xl text-[#0a57a4] mb-1">{rate.price / 100}€ <span className="text-base font-normal">TTC</span></p>
                  <p className="text-xs text-[#858275] mb-3">
                    HT : {(rate.price / 100 / 1.20).toFixed(2)}€ | TVA 20% : {(rate.price / 100 - rate.price / 100 / 1.20).toFixed(2)}€
                  </p>
                  <p className="text-xs text-[#858275]">
                    {rate.duration_hours}h avec carburant
                  </p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4" data-testid="basse-saison-pricing">
              {basseSaisonRates.map((rate, i) => (
                <motion.div
                  key={rate.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#FDFBF7] p-4 sm:p-6 text-center rounded-sm border border-[#dec5ae]/30 hover:border-[#099dbb] transition-colors"
                >
                  <p className="text-xs sm:text-sm text-[#858275] mb-2">{rate.duration_label}</p>
                  <p className="font-heading text-2xl sm:text-4xl text-[#0a57a4] mb-1">{rate.price / 100}€ <span className="text-xs sm:text-sm font-normal">TTC</span></p>
                  <p className="text-[9px] sm:text-[10px] text-[#858275] mb-1">HT : {(rate.price / 100 / 1.20).toFixed(2)}€ | TVA : {(rate.price / 100 - rate.price / 100 / 1.20).toFixed(2)}€</p>
                  <p className="text-[10px] sm:text-xs text-[#858275]">{rate.time_slot}</p>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/reservation">
              <Button 
                data-testid="pricing-reserve-btn"
                className="bg-[#099dbb] hover:bg-[#0a57a4] text-white px-8 py-6 text-lg"
              >
                Réserver maintenant
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-[#0a57a4] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-[#099dbb] text-xs tracking-[0.2em] uppercase mb-4 font-semibold">{t('reviews.title', lang)}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-white">
              {t('reviews.title', lang)}
            </h2>
            <div className="flex items-center justify-center gap-2 mt-4">
              <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-white/80 text-sm">5.0/5 — Google</span>
            </div>
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJ8_wjfOvnEBIROX2X99uYHR0"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white text-sm transition-all"
            >
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              Laisser un avis Google
            </a>
          </motion.div>
        </div>

        <div className="reviews-marquee">
          <div className="reviews-track">
            {[...reviews, ...reviews].map((review, i) => (
              <div key={i} className="review-card bg-white/10 backdrop-blur-sm border-white/20">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#099dbb] text-[#099dbb]" />
                  ))}
                </div>
                <p className="text-white/90 mb-4 text-sm leading-relaxed">"{review.text}"</p>
                <p className="text-[#099dbb] font-semibold text-sm">{review.name} <span className="text-white/50 font-normal">— {review.date}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 Steps Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#099dbb] text-xs tracking-[0.2em] uppercase mb-4 font-semibold">Comment ça marche</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0a57a4]">
              Votre sortie en bateau en trois étapes
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '1',
                title: 'Choisissez votre sortie',
                description: 'Consultez les disponibilités, sélectionnez votre date et choisissez la durée de location.',
                icon: CalendarDays,
              },
              {
                step: '2',
                title: 'Retrouvez-nous au port',
                description: "Rendez-vous à Sant'Ambroggio. Avant votre départ, nous vous présentons le bateau, les consignes de sécurité et les possibilités de navigation du jour.",
                icon: MapPin,
              },
              {
                step: '3',
                title: 'Profitez de la Balagne',
                description: "Partez à votre rythme dans la zone autorisée, découvrez la côte depuis la mer et retrouvez-nous au port à l'heure convenue.",
                icon: Anchor,
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center"
              >
                <div className="relative mx-auto mb-6">
                  <div className="w-16 h-16 mx-auto bg-[#0a57a4]/5 rounded-full flex items-center justify-center">
                    <item.icon className="w-7 h-7 text-[#0a57a4]" />
                  </div>
                  <span className="absolute -top-1 -right-1 w-7 h-7 bg-[#099dbb] text-white text-xs font-bold rounded-full flex items-center justify-center" style={{ left: '50%', marginLeft: '16px' }}>
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading text-lg text-[#0a57a4] mb-3">{item.title}</h3>
                <p className="text-sm text-[#858275] leading-relaxed max-w-xs mx-auto">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Itineraries Section */}
      <section id="itineraires" className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#099dbb] text-xs tracking-[0.2em] uppercase mb-4 font-semibold">{t('itineraries.from', lang)}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0a57a4] mb-4">
              {t('itineraries.title', lang)}
            </h2>
            <p className="text-[#858275] max-w-2xl mx-auto">
              {t('itineraries.subtitle', lang)}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {itineraries.map((itinerary, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={`/${itinerary.slug}`} className="itinerary-card rounded-sm block" data-testid={`itinerary-${itinerary.slug}`}>
                  <img src={itinerary.image} alt={itinerary.name} />
                  <div className="itinerary-overlay">
                    <h3 className="font-heading text-lg text-white mb-1">{itinerary.name}</h3>
                    <div className="flex items-center gap-2 text-white/80 text-xs">
                      {itinerary.distance && <span>{itinerary.distance}</span>}
                      {itinerary.distance && itinerary.time && <span>•</span>}
                      {itinerary.time && <span>{itinerary.time}</span>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Spots Map Banner */}
      <section className="relative overflow-hidden" data-testid="spots-map-section">
        <div className="relative">
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1630512996510-c6a301d874cc?auto=format&fit=crop&w=1920&q=80"
              alt="Vue aérienne criques Balagne"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a57a4]/90 via-[#0a57a4]/70 to-[#0a57a4]/40" />
          </div>
          {/* Content */}
          <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
            <div className="max-w-xl">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 mb-4">
                  <div className="w-8 h-[2px] bg-[#099dbb]" />
                  <span className="text-[#099dbb] text-xs tracking-[0.2em] uppercase font-semibold">Explorer la Balagne</span>
                </div>
                <h2 className="font-heading text-3xl lg:text-4xl text-white mb-4 leading-tight">
                  Notre carte des<br />meilleurs spots
                </h2>
                <p className="text-white/80 mb-8 leading-relaxed text-sm lg:text-base">
                  Criques secrètes, plages de sable fin, eaux turquoise... Découvrez notre sélection des plus beaux mouillages entre Calvi et l'Ile Rousse, accessibles uniquement par la mer.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://www.google.com/maps/d/viewer?mid=1mk_JXbrAefSMW3y3cNdLQD20LZIY9TU"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#099dbb] hover:bg-[#099dbb]/80 text-white font-semibold transition-all hover:translate-y-[-2px] hover:shadow-lg hover:shadow-[#099dbb]/25"
                    data-testid="spots-map-link"
                  >
                    <MapPin className="w-4 h-4" />
                    Voir la carte complète
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-[#099dbb] text-xs tracking-[0.2em] uppercase mb-4 font-semibold">Vos questions</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#0a57a4]">
              Foire aux questions
            </h2>
          </motion.div>

          <div className="space-y-3" data-testid="faq-section">
            {[
              { q: "Faut-il un permis bateau ?", a: "Non, nos bateaux se conduisent sans permis bateau. Avant le départ, nous vous présentons leur fonctionnement, les manœuvres et les consignes à respecter pendant votre sortie." },
              { q: "Peut-on louer si l'on n'a jamais conduit de bateau ?", a: "Oui. Nous prenons le temps de vous expliquer les commandes et les principales manœuvres avant le départ. Vous pouvez nous poser toutes vos questions lors de cette présentation." },
              { q: "Combien de personnes peuvent monter à bord ?", a: "Nos bateaux accueillent jusqu'à 5 personnes au total. Indiquez l'ensemble des participants lors de votre réservation." },
              { q: "Quelles durées de location proposez-vous ?", a: "Vous pouvez choisir une sortie entre 3 heures et 6 heures. Les créneaux disponibles et le tarif correspondant sont indiqués lors de la réservation." },
              { q: "Le carburant est-il inclus ?", a: "Oui, le carburant est inclus dans le prix de la location." },
              { q: "Où se fait le départ ?", a: "Le départ s'effectue au port de Sant'Ambroggio, à Lumio, entre Calvi et L'Île-Rousse. Le bateau doit être ramené à ce même port à l'heure prévue." },
              { q: "Peut-on aller partout avec le bateau ?", a: "La sortie s'effectue dans la zone de navigation autorisée, entre le Phare de la Revellata et le Phare de la Pietra. Avant votre départ, nous vous indiquons les destinations possibles, les secteurs à éviter et les consignes à suivre selon les conditions du jour." },
              { q: "Que faut-il prévoir pour la sortie ?", a: "Prévoyez de l'eau, une protection solaire, un chapeau, votre maillot de bain et de quoi protéger vos effets personnels des éclaboussures. Vous pouvez également prévoir votre pique-nique." },
              { q: "Que se passe-t-il en cas de météo défavorable ?", a: "Les départs dépendent des conditions météorologiques et de l'état de la mer. Si les conditions ne permettent pas la sortie, nous vous contactons. Les modalités de report ou d'annulation sont précisées dans nos conditions de location." },
              { q: "Peut-on rejoindre une plage avec le bateau ?", a: "Vous pouvez découvrir les plages depuis la mer et vous arrêter dans les secteurs autorisés. Le bateau ne doit pas être échoué sur le sable ni approché des rochers. Les règles d'approche et de mouillage vous sont expliquées avant le départ." },
            ].map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ITINERARY_IMAGES.santAmbroggio})` }}
        />
        <div className="absolute inset-0 bg-[#0a57a4]/80" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              {lang === 'fr' ? "Prêt pour l'aventure ?" : lang === 'en' ? "Ready for adventure?" : lang === 'it' ? "Pronti per l'avventura?" : "Bereit für das Abenteuer?"}
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              {lang === 'fr' ? "Réservez dès maintenant votre bateau sans permis et partez à la découverte des plus belles plages de la Balagne." : lang === 'en' ? "Book your no-license boat now and set off to discover the most beautiful beaches of Balagne." : lang === 'it' ? "Prenotate ora la vostra barca senza patente e partite alla scoperta delle più belle spiagge della Balagne." : "Buchen Sie jetzt Ihr führerscheinfreies Boot und entdecken Sie die schönsten Strände der Balagne."}
            </p>
            <Link to="/reservation">
              <Button 
                data-testid="cta-reserve-btn"
                className="bg-[#099dbb] hover:bg-white hover:text-[#0a57a4] text-white px-10 py-6 text-lg"
              >
                {t('hero.bookNow', lang)}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact / Footer */}
      <footer id="contact" className="bg-[#0a57a4] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="mb-6">
                <img src={LOGO_FOOTER} alt="A Bella Barca" className="h-20" />
              </div>
              <p className="text-white/70 mb-6 leading-relaxed">
                {t('footer.description', lang)}
              </p>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/a_bella_barca/" target="_blank" rel="noopener noreferrer" 
                   className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#099dbb] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.facebook.com/people/A-Bella-Barca/61575603587342/" target="_blank" rel="noopener noreferrer" 
                   className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#099dbb] transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-heading text-lg mb-6">Contact</h4>
              <div className="space-y-4">
                <a href="tel:+33687999639" className="flex items-center gap-3 text-white/70 hover:text-[#099dbb] transition-colors">
                  <Phone className="w-5 h-5" />
                  <span>06 87 99 96 39</span>
                </a>
                <a href="mailto:contact@abellabarca.com" className="flex items-center gap-3 text-white/70 hover:text-[#099dbb] transition-colors">
                  <Mail className="w-5 h-5" />
                  <span>contact@abellabarca.com</span>
                </a>
                <div className="flex items-start gap-3 text-white/70">
                  <MapPin className="w-5 h-5 mt-1" />
                  <span>Port de Sant'Ambroggio<br/>20260 Lumio, Corse</span>
                </div>
                <div className="flex items-start gap-3 text-white/70">
                  <Clock className="w-5 h-5 mt-1" />
                  <span>Lun - Dim : 9h - 21h</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-heading text-lg mb-6">{t('footer.quickLinks', lang)}</h4>
              <div className="space-y-3">
                <a href="#bateau" className="block text-white/70 hover:text-[#099dbb] transition-colors">{t('nav.boats', lang)}</a>
                <a href="#tarifs" className="block text-white/70 hover:text-[#099dbb] transition-colors">{t('nav.rates', lang)}</a>
                <a href="#itineraires" className="block text-white/70 hover:text-[#099dbb] transition-colors">{t('nav.itineraries', lang)}</a>
                <Link to="/reservation" className="block text-white/70 hover:text-[#099dbb] transition-colors">{t('nav.book', lang)}</Link>
                <Link to="/contact" className="block text-white/70 hover:text-[#099dbb] transition-colors">{t('nav.contact', lang)}</Link>
                <Link to="/admin" className="block text-white/70 hover:text-[#099dbb] transition-colors">Admin</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8">
            {/* Payment icons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-6">
              <span className="text-white/50 text-sm">{t('footer.securePay', lang)}</span>
              <div className="flex items-center gap-3">
                {/* CB */}
                <div className="bg-white rounded px-2 py-1 flex items-center justify-center h-8">
                  <span className="text-[#0a57a4] font-bold text-xs tracking-tight">CB</span>
                </div>
                {/* Visa */}
                <div className="bg-white rounded px-2 py-1 flex items-center justify-center h-8">
                  <svg className="h-5" viewBox="0 0 48 16" fill="none"><path d="M17.4 1.2L11.2 14.8H7.4L4.4 4.2C4.2 3.4 4 3.2 3.4 2.8C2.4 2.2 0.8 1.8 0 1.4L0.2 1.2H6.2C7 1.2 7.6 1.8 7.8 2.6L9.2 10L12.8 1.2H17.4ZM33.2 10.2C33.2 6.6 28.2 6.4 28.2 4.8C28.2 4.2 28.8 3.8 29.8 3.6C30.8 3.4 32.6 3.6 34 4.2L34.6 1.6C33.8 1.4 32.6 1 31.2 1C27 1 24 3.2 24 6.2C24 8.4 26 9.6 27.4 10.4C28.8 11.2 29.4 11.6 29.4 12.2C29.4 13.2 28.2 13.6 27 13.6C25.4 13.6 23.6 13 22.6 12.4L22 15C23.2 15.6 25 15.8 26.8 15.8C31.2 15.8 33.2 13.6 33.2 10.2ZM42.8 14.8H46.4L43.2 1.2H40.2C39.4 1.2 38.8 1.6 38.6 2.4L32.4 14.8H36.2L37 12.4H41.6L42.8 14.8ZM38 9.6L40 4.4L41 9.6H38ZM23 1.2L20 14.8H16.4L19.4 1.2H23Z" fill="#1A1F71"/></svg>
                </div>
                {/* Mastercard */}
                <div className="bg-white rounded px-2 py-1 flex items-center justify-center h-8">
                  <svg className="h-5" viewBox="0 0 32 20" fill="none"><circle cx="12" cy="10" r="8" fill="#EB001B"/><circle cx="20" cy="10" r="8" fill="#F79E1B"/><path d="M16 3.6C17.8 5 19 7.4 19 10C19 12.6 17.8 15 16 16.4C14.2 15 13 12.6 13 10C13 7.4 14.2 5 16 3.6Z" fill="#FF5F00"/></svg>
                </div>
                {/* Amex */}
                <div className="bg-white rounded px-2 py-1 flex items-center justify-center h-8">
                  <span className="text-[#006FCF] font-bold text-[10px] tracking-tight leading-none">AMEX</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-white/50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span className="text-white/50 text-sm font-semibold">SQUARE</span>
              </div>
            </div>
            
            <div className="text-center text-white/50 text-sm space-y-2">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link to="/cgv" className="hover:text-[#099dbb] transition-colors">{t('footer.cgv', lang)}</Link>
                <span>|</span>
                <Link to="/politique-confidentialite" className="hover:text-[#099dbb] transition-colors">{t('footer.privacy', lang)}</Link>
                <span>|</span>
                <Link to="/mentions-legales" className="hover:text-[#099dbb] transition-colors">{t('footer.legal', lang)}</Link>
              </div>
              <p>© 2026 A BELLA BARCA. Tous droits réservés. | Conception © 2025 <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#099dbb] hover:text-white transition-colors">WEBISULA</a></p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
